/**
 * Hospitals API Client
 * Talks to the backend endpoints shipped in Task 3.1.
 */

import axios from '@/config/axios';
import type { SpecialtyNameTranslations } from '@/services/api/specialtiesApi';

/**
 * One specialty offered by a hospital — the canonical slug (the value to
 * submit / filter doctors by) plus per-locale display names for the label.
 */
export interface HospitalSpecialtyOption {
  slug: string;
  nameTranslations: SpecialtyNameTranslations;
}

export interface HospitalDto {
  id: string;
  organizationId: string;
  name: string;
  typeCode: string;
  address: string;
  city: string;
  country: string;
  /** IANA time zone of the hospital's wall clock (appointment dates and times), from its country; "UTC" when unmapped. */
  timeZone: string;
  // Currency moved to OrganizationDto in migration 20260519000001. VAT moved
  // to the per-org `vat_rates` catalog (hospital column dropped in 20260519000003).
  primaryContactName: string;
  primaryContactEmail: string;
  primaryContactPhone: string | null;
  isActive: boolean;
  triageEnabled: boolean;
  autoExpireQueueHours: number;
  createdAt: string;
  updatedAt: string;
}

export interface HospitalTypeDto {
  code: string;
  labelI18nKey: string;
  countryCode: string | null;
  sortOrder: number;
}

export interface ListHospitalsParams {
  page?: number;
  pageSize?: number;
  organizationId?: string;
  isActive?: boolean;
  search?: string;
}

export interface PaginatedHospitals {
  items: HospitalDto[];
  total: number;
  page: number;
  pageSize: number;
}

export interface CreateHospitalPayload {
  organizationId: string;
  name: string;
  typeCode: string;
  address: string;
  city: string;
  country: string;
  primaryContactName: string;
  primaryContactEmail: string;
  primaryContactPhone?: string | null;
  triageEnabled?: boolean;
  autoExpireQueueHours?: number;
}

export interface UpdateHospitalPayload {
  name?: string;
  typeCode?: string;
  address?: string;
  city?: string;
  country?: string;
  primaryContactName?: string;
  primaryContactEmail?: string;
  primaryContactPhone?: string | null;
  isActive?: boolean;
  triageEnabled?: boolean;
  autoExpireQueueHours?: number;
}

export const hospitalsApi = {
  async list(params?: ListHospitalsParams): Promise<PaginatedHospitals> {
    const response = await axios.get<PaginatedHospitals>('/hospitals', { params });
    return response.data;
  },

  async getById(id: string): Promise<HospitalDto> {
    const response = await axios.get<HospitalDto>(`/hospitals/${id}`);
    return response.data;
  },

  async getMine(): Promise<HospitalDto[]> {
    const response = await axios.get<HospitalDto[]>('/hospitals/mine');
    return response.data;
  },

  async create(payload: CreateHospitalPayload): Promise<HospitalDto> {
    const response = await axios.post<HospitalDto>('/hospitals', payload);
    return response.data;
  },

  async update(id: string, payload: UpdateHospitalPayload): Promise<HospitalDto> {
    const response = await axios.patch<HospitalDto>(`/hospitals/${id}`, payload);
    return response.data;
  },

  async listTypes(countryCode?: string): Promise<HospitalTypeDto[]> {
    const response = await axios.get<HospitalTypeDto[]>('/hospital-types', {
      params: countryCode ? { countryCode } : undefined,
    });
    return response.data;
  },

  /**
   * Specialties of active doctors currently assigned to the given hospital
   * (derived from doctor_hospitals + doctors.specialty), each enriched with
   * the organization catalog's per-locale display names. Powers the
   * consultation-type step in the New Appointment dialog — stays in sync with
   * actual staff. The `slug` is the value to submit; `nameTranslations` is
   * for the label.
   */
  async getSpecialties(hospitalId: string): Promise<HospitalSpecialtyOption[]> {
    const response = await axios.get<HospitalSpecialtyOption[]>(`/hospitals/${hospitalId}/specialties`);
    return response.data;
  },

  /**
   * Picker-shaped list of active doctors at the given hospital. Optional
   * specialty filter to drive the "pick a doctor for this consultation" step.
   */
  async getDoctors(hospitalId: string, specialty?: string): Promise<HospitalDoctorPickerItemDto[]> {
    const response = await axios.get<HospitalDoctorPickerItemDto[]>(`/hospitals/${hospitalId}/doctors`, {
      params: specialty ? { specialty } : undefined,
    });
    return response.data;
  },

  /**
   * Picker-shaped list of active nurses at the given hospital — used for
   * vaccination and treatment appointments.
   */
  async getNurses(hospitalId: string): Promise<HospitalNursePickerItemDto[]> {
    const response = await axios.get<HospitalNursePickerItemDto[]>(`/hospitals/${hospitalId}/nurses`);
    return response.data;
  },
};

// `userId` is what staff-assignment payloads key on (ER care team writes
// `staffUserId`); `id` is the doctor/nurse profile row.
export interface HospitalDoctorPickerItemDto {
  id: string;
  userId: string;
  firstName: string;
  lastName: string;
  specialty: string;
}

export interface HospitalNursePickerItemDto {
  id: string;
  userId: string;
  firstName: string;
  lastName: string;
  department: string;
}
